import { Link, Outlet } from "react-router-dom";
import type { PropsWithChildren } from "react";

import { useAdminSession } from "./admin-guard";
import { ADMIN_AREAS, type AdminRoutePermission } from "./admin-areas";

export { ADMIN_AREAS } from "./admin-areas";
export type { AdminArea, AdminRoutePermission } from "./admin-areas";

export function firstAllowedAdminPath(permissions: readonly string[]): string | null {
  return ADMIN_AREAS.find((area) => permissions.includes(area.permission))?.path ?? null;
}

function areaLabel(path: string | null): string {
  return ADMIN_AREAS.find((area) => area.path === path)?.label ?? "uma área autorizada";
}

function AdminRouteForbidden({ firstAllowedPath }: { readonly firstAllowedPath: string | null }) {
  return (
    <section aria-labelledby="admin-route-forbidden-title" className="grid max-w-2xl gap-4 rounded-[var(--radius-surface)] border border-[var(--app-border)] bg-[var(--color-surface)] p-6">
      <h1 className="m-0 text-2xl font-bold" id="admin-route-forbidden-title">Acesso não autorizado</h1>
      <p className="m-0 text-[var(--color-muted)]">Sua sessão administrativa não possui a permissão necessária para esta área.</p>
      {firstAllowedPath ? (
        <Link className="w-fit font-bold text-[var(--color-action)] underline" to={firstAllowedPath}>
          Ir para {areaLabel(firstAllowedPath)}
        </Link>
      ) : (
        <Link className="w-fit font-bold text-[var(--color-action)] underline" to="/minha-conta">
          Voltar para minha conta
        </Link>
      )}
    </section>
  );
}

export function AdminRouteGuard({
  children,
  permission,
}: PropsWithChildren<{ readonly permission: AdminRoutePermission }>) {
  const session = useAdminSession();

  if (!session.permissions.includes(permission)) {
    return <AdminRouteForbidden firstAllowedPath={firstAllowedAdminPath(session.permissions)} />;
  }

  return children ?? <Outlet />;
}
